import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateSavedSearchInput,
  UpdateSavedSearchInput,
} from '@tabbio/types';
import {
  createSavedSearch,
  deleteSavedSearch,
  fetchSavedSearches,
  updateSavedSearch,
} from '../lib/saved-searches-api';
import { QUERY_KEYS } from '../lib/query-keys';
import { useWorkspace } from '../providers/useWorkspace';

export function useSavedSearches(enabled = true) {
  const { companyId } = useWorkspace();

  return useQuery({
    queryKey: QUERY_KEYS.LIBRARY_SAVED_SEARCHES(companyId),
    queryFn: fetchSavedSearches,
    enabled: Boolean(companyId) && enabled,
    staleTime: 15_000,
  });
}

export function useCreateSavedSearch() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (input: CreateSavedSearchInput) => createSavedSearch(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_SAVED_SEARCHES(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_OVERVIEW(companyId),
      });
    },
  });
}

export function useUpdateSavedSearch() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateSavedSearchInput }) =>
      updateSavedSearch(id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_SAVED_SEARCHES(companyId),
      });
    },
  });
}

export function useDeleteSavedSearch() {
  const queryClient = useQueryClient();
  const { companyId } = useWorkspace();

  return useMutation({
    mutationFn: (id: string) => deleteSavedSearch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_SAVED_SEARCHES(companyId),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.LIBRARY_OVERVIEW(companyId),
      });
    },
  });
}
